import { useEffect, useRef, useState } from "react";
import { facescan } from "careplix-scan-sdk";
import { useLang } from "../OneHealthContext";
import { SCAN_DURATION_SEC, SCAN_CLIENT } from "../config";
import { createToken, analyzeScan } from "../careplix";
import { fetchWorkerPhoto, getDeviceKey, hasRosterAccess, listDepartments, matchWorker, onboardWorker, syncRoster, verifyWorker } from "../roster";
import { loadModels, descriptorFrom, descriptorFromBlob, distance, MATCH_THRESHOLD } from "../faceid";

// The camera is blocked inside an embedded iframe (e.g. the editor preview
// pane). It only works when the page is the top-level document in its own tab.
const isEmbedded = (() => {
  try { return window.self !== window.top; } catch (_) { return true; }
})();

/* Ideal camera resolution for the current orientation. A landscape 1280×720
   feed on a portrait screen gets massively cropped by object-fit:cover and the
   face looks far too close — ask for a portrait feed instead. */
const idealCamRes = () =>
  window.innerHeight > window.innerWidth
    ? { width: { ideal: 720, min: 360 }, height: { ideal: 1280, min: 640 } }
    : { width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 360 } };

/* Camera resolution for the rPPG SCAN phase specifically. The SDK copies every
   frame to a canvas and reads pixels back on each animation frame — at 720×1280
   that drops weaker tablets below the SDK's 10fps minimum, which kills the scan
   at 100% with CMSCN02. 480×640 keeps the portrait aspect (no over-crop) while
   quartering the per-frame pixel cost; rPPG needs light intensity, not detail. */
const scanCamRes = () =>
  window.innerHeight > window.innerWidth
    ? { width: { ideal: 480, min: 320 }, height: { ideal: 640, min: 480 } }
    : { width: { ideal: 640, min: 480 }, height: { ideal: 480, min: 320 } };

/* FACE SCAN — two phases in one camera session:
   1) IDENTIFY: recognise the worker against the directory and authenticate them
      (simulated). If found → verified card. If NOT found → a manual-entry form
      (name / age / height / weight). Either way a "Start face scan" button begins
      the vitals scan — and the CarePlix create-token is fired at that click.
   2) SCAN: the real CarePlix rPPG capture. NO fabricated heart rate is shown while
      measuring; progress is a horizontal bar + a small completion %. The only
      vitals ever displayed are the real ones from add-scan (on the Result screen).
   Real vitals are accepted ONLY on a valid add-scan (2xx + heart_rate > 0);
   any failure shows a retry prompt — vitals are NEVER fabricated. */
/* `prefill` (optional): identity passed in by a host app (embed/webview mode).
   When present the IDENTIFY phase is skipped entirely — no camera preview,
   no simulated directory match, no manual form. The component boots straight
   into create-token and then the real scan with the supplied details. */
/* Age from an ISO date-of-birth string, or null when unparseable. */
function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a > 0 && a < 120 ? a : null;
}

export default function FaceScan({
  onDone,
  prefill,
  onBack,
  scanDuration = SCAN_DURATION_SEC,
  forceAssessments = null,
  onlineIdentity = false,
  startInRegistration = false,
}) {
  const { tr } = useLang();
  const [phase, setPhase] = useState(prefill ? "boot" : "identify");   // "boot" | "identify" | "scan"
  const [idState, setIdState] = useState("matching"); // "matching" | "online" | "matched" | "notfound"
  const [manual, setManual] = useState(false);      // manual-entry form open
  const [matched, setMatched] = useState(null);     // dashboard worker (face match or ID verify)
  const [regPhoto, setRegPhoto] = useState(null);   // registration profile photo (dataURL)
  const [md, setMd] = useState({ empId: "", name: "", age: "", dob: "", contact: "", gender: "", height: "", weight: "", dept: "", pulmo: false, cognitive: false });
  const [depts, setDepts] = useState(null);         // site departments from the dashboard (device mode)
  const [siteName, setSiteName] = useState("");
  const [siteId, setSiteId] = useState(null);
  const [deptError, setDeptError] = useState("");
  const [deptRetry, setDeptRetry] = useState(0);
  const [registrationMode, setRegistrationMode] = useState(!!startInRegistration);
  const [ageEntryMode, setAgeEntryMode] = useState("dob");
  const [heightUnit, setHeightUnit] = useState("cm");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");
  const [obErr, setObErr] = useState("");           // onboarding (new-worker registration) error
  const [onlineEmpId, setOnlineEmpId] = useState("");
  const [onlineBusy, setOnlineBusy] = useState(false);
  const [onlineErr, setOnlineErr] = useState("");
  const [showCaptureFallback, setShowCaptureFallback] = useState(false);
  const [starting, setStarting] = useState(false);  // create-token in flight
  const [analyzing, setAnalyzing] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const acceptWorkerRef = useRef(null); // identify-phase acceptWorker (used by ID verify)
  const shotVideoRef = useRef(null);    // registration mini-preview video
  const photoUrlRef = useRef(null);     // matched worker photo object URL (revoked on replace/unmount)
  const capturedFaceRef = useRef(null); // transient phone capture used only for the one-worker comparison
  const resultRef = useRef(null);      // captured real add-scan payload
  const scanTokenRef = useRef(null);   // scan_token from create-token (fired at Start)
  const detailsRef = useRef(null);     // identity used for the add-scan

  const videoRef = useRef();
  const canvasRef = useRef();
  const sigRef = useRef();
  const hudStateRef = useRef();
  const clockRef = useRef();
  const fillRef = useRef();
  const pctRef = useRef();
  const guideRef = useRef();
  const faceWarnRef = useRef();
  const loaderRef = useRef();
  const promptRef = useRef();
  const previewStreamRef = useRef(null);

  const clock = useRef({ elapsed: 0, finished: false });
  const sig = useRef({ phase: 0 });
  const sigBuf = useRef([]);

  const requestedDuration = Number.isFinite(Number(scanDuration)) && Number(scanDuration) > 0
    ? Number(scanDuration)
    : SCAN_DURATION_SEC;
  // The supplied CarePlix 1.3.0 SDK patch supports 15–120 second scans.
  // Clamp settings to that supported range before starting the SDK.
  const DUR = Math.min(120, Math.max(15, requestedDuration));

  // In device mode an unregistered manual entry must be tagged to a department
  // (its assessment set comes from the department, not from kiosk toggles).
  const phoneRegistering = onlineIdentity && !matched && !!getDeviceKey();
  const deptRequired = !matched && !!getDeviceKey() &&
    (phoneRegistering || (Array.isArray(depts) && depts.length > 0));
  const selectedDept = deptRequired && Array.isArray(depts) ? depts.find((d) => String(d.departmentId) === md.dept) || null : null;
  /* Registration (device mode, unmatched) collects a date of birth rather than
     an age so the record stays correct as years pass — the age is computed. */
  const registering = !matched && !!getDeviceKey();
  const dobAge = ageFromDob(md.dob);
  const effAge = registering ? (ageEntryMode === "dob" ? dobAge : Number(md.age)) : Number(md.age);
  const imperialHeightCm = (Number(heightFeet) * 12 + Number(heightInches)) * 2.54;
  const effHeight = registering && heightUnit === "imperial" ? Math.round(imperialHeightCm) : Number(md.height);
  const validAge = Number(effAge) > 0 && Number(effAge) < 120;
  const validImperialHeight = heightUnit !== "imperial" ||
    (Number(heightFeet) > 0 && Number(heightFeet) <= 8 && Number(heightInches) >= 0 && Number(heightInches) < 12);
  const manualValid = md.name.trim() && validAge && md.gender && Number(effHeight) > 0 && validImperialHeight && Number(md.weight) > 0 &&
    (!deptRequired || !!selectedDept) &&
    (!registering || (md.empId.trim() && (ageEntryMode === "dob" ? !!md.dob : Number(md.age) > 0) && !!regPhoto)) &&
    (!phoneRegistering || (!!siteId && !deptError));

  /* Bind the registration mini-preview to the main camera stream. The form can
     open before openCamera() resolves (the Register link shows while matching),
     so poll until the stream exists rather than assigning once at mount. */
  useEffect(() => {
    if (!manual || !registering || regPhoto) return;
    const attach = () => {
      const el = shotVideoRef.current;
      const src = videoRef.current?.srcObject || previewStreamRef.current;
      if (el && src && el.srcObject !== src) el.srcObject = src;
      return !!(el && el.srcObject);
    };
    if (attach()) return;
    const id = setInterval(() => { if (attach()) clearInterval(id); }, 300);
    return () => clearInterval(id);
  }, [manual, registering, regPhoto]);

  // Fetch the site's department list (with assessment sets) when the manual form opens.
  useEffect(() => {
    if (!manual || matched || !getDeviceKey()) return;
    let on = true;
    setDepts(null);
    setDeptError("");
    listDepartments()
      .then((r) => {
        if (!on) return;
        const departments = Array.isArray(r.departments) ? r.departments : [];
        setDepts(departments);
        setSiteName(r.siteName || "");
        setSiteId(r.siteId ?? null);
        if (r.siteId == null || !r.siteName || departments.length === 0) {
          setDeptError(departments.length === 0 ? tr("identify.deptEmpty") : tr("identify.deptError"));
        }
      })
      .catch(() => {
        if (on) {
          setDepts([]);
          setSiteName("");
          setSiteId(null);
          setDeptError(tr("identify.deptError"));
        }
      });
    return () => { on = false; };
  }, [manual, matched, deptRetry]); // eslint-disable-line react-hooks/exhaustive-deps
  const canStart = manual && manualValid;

  const releasePreview = async () => {
    stopPreview();
    // Mobile Chromium/WebKit can report the camera as busy for a short period
    // after every track says "ended". Let the capture device settle before the
    // CarePlix SDK asks for it again.
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  const enterRegistration = async () => {
    if (!onlineIdentity) return;
    setOnlineBusy(false);
    setOnlineErr("");
    setObErr("");
    setRegPhoto(null);
    capturedFaceRef.current = null;
    setRegistrationMode(true);
    stopPreview();
    setRetryKey((k) => k + 1);
  };

  const captureOnlineIdentityPhoto = async () => {
    if (onlineBusy) return;
    setOnlineBusy(true); setOnlineErr("");
    try {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth) {
        throw new Error("preview is not ready");
      }
      const descriptor = await descriptorFrom(video, { thorough: true });
      const photo = capturePhoto(480);
      if (!descriptor || !photo) {
        setOnlineErr(tr("identify.onlineFaceRead"));
        return;
      }
      capturedFaceRef.current = descriptor;
      setRegPhoto(photo);
      await releasePreview();
      setIdState("online");
    } catch (error) {
      console.warn("identity photo capture failed", error?.message);
      setOnlineErr(tr("identify.onlineFaceRead"));
    } finally {
      setOnlineBusy(false);
    }
  };

  const captureRegistrationPhoto = async () => {
    if (onlineBusy) return;
    setOnlineBusy(true);
    setObErr("");
    try {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth) throw new Error("preview is not ready");
      const descriptor = await descriptorFrom(video, { thorough: true });
      const photo = capturePhoto(480);
      if (!descriptor || !photo) {
        setObErr(tr("identify.onlineFaceRead"));
        return;
      }
      capturedFaceRef.current = descriptor;
      setRegPhoto(photo);
    } catch (error) {
      console.warn("registration photo capture failed", error?.message);
      setObErr(tr("identify.onlineFaceRead"));
    } finally {
      setOnlineBusy(false);
    }
  };

  // Phone authentication is hands-free: as soon as a clear face is detected,
  // capture it and advance to Employee ID without requiring a button tap.
  // Keep a manual fallback for unusually dark/slow devices.
  useEffect(() => {
    if (!onlineIdentity || phase !== "identify" || idState !== "capture") return;
    let cancelled = false;
    let pollTimer = null;
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) setShowCaptureFallback(true);
    }, 8000);

    const detectAndCapture = async () => {
      if (cancelled || onlineBusy) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2 && video.videoWidth > 0) {
        try {
          const face = await descriptorFrom(video);
          if (face && !cancelled) {
            await captureOnlineIdentityPhoto();
            if (!previewStreamRef.current) return;
          }
        } catch (_) {}
      }
      if (!cancelled) pollTimer = setTimeout(detectAndCapture, 450);
    };

    pollTimer = setTimeout(detectAndCapture, 500);
    return () => {
      cancelled = true;
      clearTimeout(pollTimer);
      clearTimeout(fallbackTimer);
    };
  }, [onlineIdentity, phase, idState, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const startOnlineWorkerScan = async (worker) => {
    const workerAge = Number(worker.age ?? ageFromDob(worker.dateOfBirth));
    const details = {
      name: String(worker.name || "").trim(),
      badge: String(worker.employeeId || "").trim(),
      workerId: worker.id ?? null,
      departmentId: worker.departmentId ?? null,
      gender: worker.gender ? worker.gender[0].toUpperCase() + worker.gender.slice(1).toLowerCase() : "Other",
      age: workerAge,
      height: Number(worker.height ?? worker.heightCm),
      weight: Number(worker.weight ?? worker.weightKg),
      pulmoRequired: forceAssessments?.pulmo ?? !!worker.pulmoRequired,
      cognitiveRequired: forceAssessments?.cognitive ?? !!worker.cognitiveRequired,
    };
    detailsRef.current = details;
    setStarting(true);
    try {
      const tj = await createToken({
        employee_id: details.badge,
        is_pulmo_required: details.pulmoRequired,
      });
      if (!tj?.scan_token) throw new Error("create-token failed");
      scanTokenRef.current = tj.scan_token;
      resultRef.current = null;
      try { facescan.stopScan(); } catch (_) {}
      setPhase("scan");
    } catch (error) {
      console.error("online create-token failed", error);
      setOnlineErr(tr("scan.errStartProper"));
    } finally {
      setStarting(false);
    }
  };

  /* Phone/site mode checks one worker directly through the dashboard API, then
     compares the already-captured face with only that worker's enrollment photo.
     No roster, photo, or descriptor is written to IndexedDB. */
  const checkOnlineWorker = async () => {
    const employeeId = onlineEmpId.trim().toUpperCase();
    if (!employeeId || onlineBusy) return;
    setOnlineBusy(true); setOnlineErr("");
    try {
      const worker = await verifyWorker(employeeId);
      if (!worker || worker.id == null) {
        setOnlineErr(tr("identify.onlineNotFound"));
        return;
      }
      const photoBlob = await fetchWorkerPhoto(worker.id);
      if (!photoBlob) {
        setOnlineErr(tr("identify.onlinePhotoMissing"));
        return;
      }
      const liveDesc = capturedFaceRef.current;
      const enrolledDesc = await descriptorFromBlob(photoBlob);
      if (!liveDesc || !enrolledDesc) {
        setOnlineErr(tr("identify.onlineFaceRead"));
        return;
      }
      if (distance(liveDesc, enrolledDesc) > MATCH_THRESHOLD) {
        setOnlineErr(tr("identify.onlineFaceMismatch"));
        return;
      }
      await startOnlineWorkerScan(worker);
    } catch (error) {
      console.warn("online worker verification failed", error?.message);
      setOnlineErr(tr("identify.onlineError"));
    } finally {
      setOnlineBusy(false);
    }
  };

  /* Small JPEG of the current preview frame (undoes the CSS selfie mirror) —
     becomes the worker's profile photo at registration so face matching works
     on the next visit. */
  const capturePhoto = (size = 320) => {
    try {
      const v = videoRef.current;
      if (!v || !v.videoWidth) return null;
      const w = size, h = Math.round((v.videoHeight / v.videoWidth) * size);
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, w, h);
      return c.toDataURL("image/jpeg", 0.75);
    } catch (_) { return null; }
  };

  const stopPreview = () => {
    try {
      previewStreamRef.current?.getTracks().forEach((t) => {
        try { t.stop(); } catch (_) {}
      });
    } catch (_) {}
    previewStreamRef.current = null;
    for (const video of [videoRef.current, shotVideoRef.current]) {
      try {
        if (!video) continue;
        video.pause?.();
        video.srcObject = null;
        video.removeAttribute("src");
        video.load?.();
      } catch (_) {}
    }
  };

  /* ─────────────────── PHASE 0 — BOOT (embed mode only) ───────────────────
     Details came from the host app: fire create-token immediately and go
     straight to the scan. Failures show the normal retry card. */
  useEffect(() => {
    if (phase !== "boot" || !prefill) return;
    let aborted = false;
    setScanError(null);
    const details = {
      name: String(prefill.name || "").trim(),
      badge: String(prefill.employeeId || "MANUAL"),
      gender: prefill.gender || "Other",
      age: Number(prefill.age),
      height: Number(prefill.height),
      weight: Number(prefill.weight),
      pulmoRequired: false,
      cognitiveRequired: false,
    };
    detailsRef.current = details;
    (async () => {
      try {
        const tj = await createToken({ employee_id: details.badge || "MANUAL", is_pulmo_required: false });
        if (!tj?.scan_token) throw new Error("create-token failed");
        if (aborted) return;
        scanTokenRef.current = tj.scan_token;
        resultRef.current = null;
        setPhase("scan");
      } catch (err) {
        console.error("embed create-token failed", err);
        if (!aborted) setScanError(tr("scan.errStartProper"));
      }
    })();
    return () => { aborted = true; };
  }, [phase, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─────────────────── PHASE 1 — IDENTIFY (simulated) ─────────────────── */
  useEffect(() => {
    if (phase !== "identify") return;
    let aborted = false;
    let matchTimer = null;
    setIdState("matching"); setManual(false); setScanError(null);
    setMatched(null); setRegPhoto(null);
    setShowCaptureFallback(false);
    capturedFaceRef.current = null;

    // The dashboard worker matched → prefill + lock the assessment set that was
    // configured for them at the admin dashboard.
    const acceptWorker = (w) => {
      if (aborted) return;
      let photoObjUrl = null;
      try {
        if (photoUrlRef.current) { URL.revokeObjectURL(photoUrlRef.current); photoUrlRef.current = null; }
        if (w.photoBlob) { photoObjUrl = URL.createObjectURL(w.photoBlob); photoUrlRef.current = photoObjUrl; }
      } catch (_) {}
      setMatched({ ...w, photoObjUrl });
      // Prefill the full demographic set from the authoritative record — a
      // 409-conflict recovery lands here with only DOB typed, so the age must
      // come from the record (or its dateOfBirth) or Start stays disabled.
      setMd((m) => ({
        ...m,
        name: w.name || "",
        age: w.age != null ? String(w.age) : (ageFromDob(w.dateOfBirth) != null ? String(ageFromDob(w.dateOfBirth)) : m.age),
        gender: w.gender ? w.gender[0].toUpperCase() + w.gender.slice(1).toLowerCase() : m.gender,
        height: w.height != null ? String(w.height) : (w.heightCm != null ? String(w.heightCm) : m.height),
        weight: w.weight != null ? String(w.weight) : (w.weightKg != null ? String(w.weightKg) : m.weight),
        pulmo: !!w.pulmoRequired, cognitive: !!w.cognitiveRequired,
      }));
      setIdState("matched");
    };
    acceptWorkerRef.current = acceptWorker;

    // Device-local face matching against the cached roster (recommended flow:
    // match → on miss, one delta sync + re-embed → rematch → manual entry).
    const recognizeLoop = async () => {
      try { await loadModels(); } catch (e) {
        console.warn("face models failed to load", e?.message);
        if (!aborted) setIdState("notfound");
        return;
      }
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const deadline = performance.now() + 10000;
      let resynced = false;
      let frame = 0;
      let lastId = null, streak = 0; // require 2 consecutive frames on the same worker
      while (!aborted && performance.now() < deadline) {
        const v = videoRef.current;
        if (!v || v.readyState < 2) { await sleep(250); continue; }
        let desc = null;
        // Fast 320px pass on most frames; the expensive 512px fallback pass only
        // every 3rd frame so a missed detection can't saturate the kiosk CPU.
        try { desc = await descriptorFrom(v, { thorough: frame % 3 === 2 }); } catch (_) {}
        frame++;
        if (aborted) return;
        if (desc) {
          const m = await matchWorker(desc);
          if (aborted) return;
          if (m) {
            streak = m.worker.id === lastId ? streak + 1 : 1;
            lastId = m.worker.id;
            if (streak >= 2) { acceptWorker(m.worker); return; }
            await sleep(150); // confirmation frame, quickly
            continue;
          }
          lastId = null; streak = 0;
          if (!resynced) {
            resynced = true; // roster may be stale — one delta sync, then keep trying
            try { await syncRoster(); } catch (e) { console.warn("delta sync failed", e?.message); }
          }
        } else { lastId = null; streak = 0; }
        await sleep(400);
      }
      if (!aborted) setIdState("notfound");
    };

    // Open the identify camera with retries: right after page load (or right
    // after the permission prompt is granted) the device can be transiently
    // busy / not ready (NotReadableError, AbortError), which used to surface a
    // spurious "camera not granted" error on first open. Real permission
    // denials fail immediately without retrying.
    const openCamera = async () => {
      const constraints = {
        video: { facingMode: "user", ...idealCamRes() },
        audio: false,
      };
      let lastErr = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          return await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          lastErr = err;
          // Permission actually denied → no point retrying.
          if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) throw err;
          if (aborted) throw err;
          await new Promise((r) => setTimeout(r, 450 * (attempt + 1)));
          if (aborted) throw err;
        }
      }
      throw lastErr;
    };

    (async () => {
      try {
        const stream = await openCamera();
        if (aborted) { stream.getTracks().forEach((t) => t.stop()); return; }
        previewStreamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play?.().catch(() => {}); }
        if (onlineIdentity && registrationMode) {
          setIdState("register");
          setManual(true);
        } else if (onlineIdentity) {
          // Capture first. The preview is released before Employee ID entry, so
          // the SDK is the sole camera owner when the real scan begins.
          setIdState("capture");
        } else if (hasRosterAccess()) {
          // Device key OR selected site → real device-local face recognition
          // against the synced roster.
          recognizeLoop();
        } else {
          // Standalone demo (no key, no site): identity can't be verified — go
          // to manual entry after a beat. A verified identity is never fabricated.
          matchTimer = setTimeout(() => { if (!aborted) setIdState("notfound"); }, 1300);
        }
      } catch (err) {
        console.warn("identify camera failed", err?.message);
        if (!aborted) setScanError(tr("scan.errCameraStart"));
      }
    })();

    return () => { aborted = true; if (matchTimer) clearTimeout(matchTimer); stopPreview(); };
  }, [phase, retryKey, onlineIdentity, registrationMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke the matched-photo object URL when the component unmounts.
  useEffect(() => () => {
    try { if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current); } catch (_) {}
  }, []);

  const buildDetails = () => ({
    name: md.name.trim(),
    badge: matched?.employeeId || "MANUAL",
    workerId: matched?.id ?? null,
    departmentId: matched?.departmentId ?? selectedDept?.departmentId ?? null,
    gender: md.gender,
    age: Number(effAge), height: Number(effHeight), weight: Number(md.weight),
    // Assessment set: matched/verified worker → their dashboard config; manual
    // entry in device mode → the selected department's set; standalone → toggles.
    pulmoRequired: forceAssessments?.pulmo ?? (matched ? !!matched.pulmoRequired : selectedDept ? !!selectedDept.pulmoRequired : !!md.pulmo),
    cognitiveRequired: forceAssessments?.cognitive ?? (matched ? !!matched.cognitiveRequired : selectedDept ? !!selectedDept.cognitiveRequired : !!md.cognitive),
  });

  // Start face scan → fire create-token now, then hand off the camera to the scan.
  const startScan = async () => {
    if (!canStart || starting) return;
    const details = buildDetails();
    setStarting(true);
    // Device mode, unregistered worker → onboard them at the dashboard first
    // (validates the department against this device's site, returns the created
    // worker incl. its authoritative pulmo/cognitive flags + employeeId).
    if (!matched && selectedDept && getDeviceKey()) {
      // Blocking: without a successful onboarding the screening couldn't be
      // attributed to a worker on the dashboard — surface the error and let the
      // worker retry instead of silently running an unsynced screening.
      setObErr("");
      const empId = md.empId.trim().toUpperCase();
      try {
        /* Registration payload mirrors the kiosk's POST /api/v1/device/workers:
           real employee ID + date of birth (age computed server-side), plus a
           profile photo from the live preview so face matching works on the
           worker's next visit. */
        const w = await onboardWorker({
          employeeId: empId || undefined,
          name: details.name,
          departmentId: selectedDept.departmentId,
          dateOfBirth: ageEntryMode === "dob" ? (md.dob || undefined) : undefined,
          gender: (details.gender || "").toLowerCase(),
          age: details.age, height: details.height, weight: details.weight,
          heightCm: details.height, weightKg: details.weight,
          contact: md.contact.trim() || undefined,
          photoData: regPhoto || capturePhoto() || undefined,
        });
        if (!w || w.id == null) throw new Error("onboarding rejected");
        details.workerId = w.id;
        details.badge = w.employeeId || empId || details.badge;
        if (typeof w.pulmoRequired === "boolean") details.pulmoRequired = w.pulmoRequired;
        if (typeof w.cognitiveRequired === "boolean") details.cognitiveRequired = w.cognitiveRequired;
      } catch (e) {
        console.warn("worker onboarding failed", e?.message);
        /* Already on record at this site — fetch the authoritative record
           rather than pushing a duplicate or trusting what was just typed. */
        if (/\b409\b/.test(e?.message || "") && empId) {
          if (onlineIdentity) {
            try {
              const existing = await verifyWorker(empId);
              if (!existing || existing.id == null) throw new Error("existing worker unavailable");
              const enrolledPhoto = await fetchWorkerPhoto(existing.id);
              const liveDesc = capturedFaceRef.current;
              const enrolledDesc = enrolledPhoto ? await descriptorFromBlob(enrolledPhoto) : null;
              if (!liveDesc || !enrolledDesc) {
                setObErr(tr("identify.onlinePhotoMissing"));
                setStarting(false);
                return;
              }
              if (distance(liveDesc, enrolledDesc) > MATCH_THRESHOLD) {
                setObErr(tr("identify.onlineFaceMismatch"));
                setStarting(false);
                return;
              }
              setObErr(tr("identify.alreadyExists"));
              setStarting(false);
              await releasePreview();
              await startOnlineWorkerScan(existing);
              return;
            } catch (verifyError) {
              console.warn("existing worker recovery failed", verifyError?.message);
              setObErr(tr("identify.alreadyExists"));
              setStarting(false);
            }
            return;
          }
          try {
            const v = await verifyWorker(empId);
            if (v && v.id != null) {
              setStarting(false);
              acceptWorkerRef.current?.(v);
              setObErr(tr("identify.alreadyExists"));
              return;
            }
          } catch (_) {}
          setObErr(tr("identify.alreadyExists"));
        } else {
          setObErr("Couldn't register you with the organisation — check the connection and try again.");
        }
        setStarting(false);
        return;
      }
    }
    detailsRef.current = details;
    try {
      const tj = await createToken({ employee_id: details.badge || "MANUAL", is_pulmo_required: details.pulmoRequired });
      if (!tj?.scan_token) throw new Error("create-token failed");
      scanTokenRef.current = tj.scan_token;
      stopPreview();
      setStarting(false);
      resultRef.current = null;
      setPhase("scan");
    } catch (err) {
      console.error("create-token failed", err);
      setStarting(false);
      setScanError(tr("scan.errStartProper"));
    }
  };

  /* ─────────────────── PHASE 2 — REAL rPPG SCAN ─────────────────── */
  useEffect(() => {
    if (phase !== "scan") return;
    let raf;
    let finished = false;
    let aborted = false;
    const timers = [];
    const later = (fn, ms) => { const id = setTimeout(fn, ms); timers.push(id); return id; };

    const L = {
      done: tr("scan.done").toUpperCase(),
      measuring: tr("scan.measuring").toUpperCase(),
      alignFace: tr("scan.alignFace").toUpperCase(),
      starting: tr("scan.starting").toUpperCase(),
    };

    clock.current = { elapsed: 0, finished: false };
    sig.current = { phase: 0 };
    sigBuf.current = [];
    setScanError(null); setAnalyzing(false); resultRef.current = null;
    if (loaderRef.current) loaderRef.current.style.display = "";
    if (faceWarnRef.current) faceWarnRef.current.style.display = "none";
    if (guideRef.current) guideRef.current.classList.remove("locked");

    // Real vitals captured — advance straight to the next module (Pulmo / PVT-B /
    // Result). No second "scan complete" screen or manual Continue: the enabled
    // screening flags decide where the flow routes.
    const captureVitals = (payload) => {
      if (finished || aborted) return;
      finished = true;
      clock.current.finished = true;
      resultRef.current = payload;
      setAnalyzing(false);
      try { facescan.stopScan(); } catch (_) {}
      const d = detailsRef.current || {};
      onDone(payload, {
        pulmo: !!d.pulmoRequired,
        cognitive: !!d.cognitiveRequired,
        scanToken: scanTokenRef.current,
        details: d,
      });
    };

    // Terminal failure — show a retry prompt, NEVER a fabricated result.
    const failScan = (msg) => {
      if (finished || aborted) return;
      finished = true;
      clock.current.finished = true;
      try { facescan.stopScan(); } catch (_) {}
      setAnalyzing(false);
      setScanError(msg || tr("scan.errStartProper"));
    };

    /* ---- real scan analysis (add-scan is run SERVER-SIDE via /api/careplix/report
       so the raw BP + banding thresholds never reach the browser; the token was
       created at Start) ---- */
    const analyzeRealScan = async ({ raw_intensity, ppg_time, average_fps }) => {
      if (aborted) return;
      setAnalyzing(true);
      clock.current.finished = true;
      const watchdog = later(() => failScan(tr("scan.errReadTime")), 55000);
      try {
        const scanToken = scanTokenRef.current;
        if (!scanToken) throw new Error("missing scan token");
        const d = detailsRef.current || {};
        const age = Number(d.age) || 30;
        const dob = `${new Date().getFullYear() - age}-01-01`;
        const report = await analyzeScan({
          scan_token: scanToken,
          posture: "resting",
          dob,
          gender: d.gender || "Other",
          metadata: {
            physiological_scores: { height: String(d.height || "170"), weight: String(d.weight || "70") },
            ppg_time, raw_intensity,
            device: `RPPG_CAREPLIX_FACE_${/iPhone|iPad|iPod|Macintosh|Mac/i.test(navigator.userAgent) ? "IOS" : "ANDROID"}`,
            fps: average_fps,
          },
          notes: { client: SCAN_CLIENT, user_id: "", emp_name: d.name || "", age: "", gender: "", height: "", weight: "" },
        });
        console.log("CarePlix report →", report?.ok ? report.verdict : (report?.error || "no report"));

        // The server returns { ok:true, verdict, wellbeing, params[] } only on a
        // genuine, valid scan — vitals are never fabricated.
        if (report && report.ok) {
          captureVitals({ ...report, employee: d });
        } else throw new Error(report?.error || "report returned no vitals");
      } catch (err) {
        console.error("CarePlix analyze failed", err);
        failScan(tr("scan.errReadVitals"));
      } finally {
        clearTimeout(watchdog);
      }
    };

    /* ---- SDK setup ---- */
    let sdkProgressed = false;
    let faceOK = false;       // is a face currently detected by the SDK?
    let framesSeen = false;   // has the camera produced ANY frame at all?
    let firstFrameMs = 0;     // timestamp of the FIRST SDK frame (for the warmup grace)
    let lastFrameMs = 0;      // timestamp of the most recent SDK frame
    let sdkFinishWatch = null;
    const stall = { elapsed: -1, atMs: 0 };
    const WARMUP_MS = 1500;   // after the first frame, give detection a moment before
                              // ever showing "face not detected"

    facescan.onFrame((fd) => {
      framesSeen = true;
      lastFrameMs = performance.now();
      if (!firstFrameMs) firstFrameMs = lastFrameMs;
      faceOK = fd.type === "scan"; // SDK reports "scan" only while a face is locked on
      if (guideRef.current) guideRef.current.classList.toggle("locked", faceOK);
      if (fd.timeElapsed != null) {
        clock.current.elapsed = Math.min(DUR, fd.timeElapsed / 1000);
        if (clock.current.elapsed > 1) sdkProgressed = true;
      }
    });

    const cameraFailed = () => {
      if (sdkProgressed || clock.current.finished) return;
      failScan(tr("scan.errCameraStart"));
    };

    facescan.onError((err, code) => {
      console.warn("FaceScan SDK error", code, err?.message);
      const msg = err?.message || "";
      // Terminal SDK outcomes — fail NOW with a specific message instead of
      // letting the generic "didn't finish in time" watchdog fire 15s later:
      // CMSCN02 = average fps < 10 (fired INSTEAD of onScanFinish at 100%),
      // CMSCN01 = signal computation failed, CMUSR02 = app went to background.
      if (code === "CMSCN02") return failScan(tr("scan.errLowFps"));
      if (code === "CMSCN01" || code === "CMUSR02") return failScan(tr("scan.errStopped"));
      const fatal = code === "CMUSR01" || code === "FCINT01" || /camera|internet|connection|network/i.test(msg);
      if (fatal) failScan(tr("scan.errStopped"));
    });
    facescan.onScanFinish((data) => { analyzeRealScan(data); });

    /* Request a sensible camera resolution so the SDK detects the face reliably.
       Orientation-aware: on portrait screens ask for a portrait feed so the
       object-fit:cover video doesn't over-crop (face looked way too close). */
    let origGUM = null;
    try {
      origGUM = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
      navigator.mediaDevices.getUserMedia = function (constraints) {
        if (constraints?.video && !constraints?.audio && typeof constraints.video === "object") {
          const res = scanCamRes();
          return origGUM({
            ...constraints,
            video: {
              ...constraints.video,
              width: res.width,
              height: res.height,
              frameRate: { ideal: 30, max: 30 },
            },
          });
        }
        return origGUM(constraints);
      };
    } catch (_) {}

    // Small delay so the identify preview fully releases the camera first.
    // The very first startScan after the camera handoff can fail transiently
    // (device still busy) — retry a couple of times before declaring failure.
    const handleStartFailure = (err, attempt) => {
      const causes = [];
      for (let current = err, depth = 0; current && depth < 5; current = current.cause, depth++) {
        if (current.message) causes.push(current.message);
      }
      const diagnostic = causes.join(" ← ");
      console.warn(`startScan failed (attempt ${attempt + 1}):`, diagnostic || err);
      if (aborted || clock.current.finished || framesSeen) return;
      // Invalid SDK arguments will fail identically forever; do not retry them
      // or misreport them as a denied camera permission.
      if (/scan duration|liveliness detection duration|video element|canvas element|draw-properties/i.test(diagnostic)) {
        failScan(tr("scan.errStartProper"));
        return;
      }
      if (attempt < 4) {
        try { facescan.stopScan(); } catch (_) {}
        later(() => startSdk(attempt + 1), 700 * (attempt + 1));
      } else cameraFailed();
    };
    const startSdk = (attempt) => {
      if (aborted || clock.current.finished) return;
      try {
        Promise.resolve(facescan.startScan({
          scanDuration: DUR,
          livelinessDetectionDuration: DUR,
          strictness: 1, // lowest valid strictness (SDK range 1–5)
          videoElement: videoRef.current,
          canvasElement: canvasRef.current,
        })).catch((err) => handleStartFailure(err, attempt));
      } catch (err) {
        handleStartFailure(err, attempt);
      }
    };
    // Phone browsers take longer to release the identify stream before the SDK
    // can acquire the same front camera.
    const startTimer = later(() => startSdk(0), onlineIdentity ? 900 : 250);

    later(() => { try { if (origGUM) navigator.mediaDevices.getUserMedia = origGUM; } catch (_) {} }, 12000);
    later(() => { if (!framesSeen && !sdkProgressed) cameraFailed(); }, 15000);
    later(() => { if (framesSeen && !sdkProgressed && !clock.current.finished) failScan(tr("scan.errStartProper")); }, 15000);

    /* ---- signal / HUD render loop (abstract capture waveform — NOT a heart rate) ---- */
    const sc = sigRef.current, sctx = sc?.getContext("2d");
    const signalAt = (ph) => 0.55 * Math.sin(ph) + 0.28 * Math.sin(ph * 2.1 + 0.7) + 0.12 * Math.sin(ph * 0.5);
    const BLUE = "#0f75c9";
    let last = performance.now();

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const elapsed = clock.current.elapsed;
      const pct = Math.max(0, Math.min(100, (elapsed / DUR) * 100));

      // Abstract scrolling "signal capture" trace at a fixed rate — it carries no
      // numeric heart-rate meaning; the real HR only appears on the Result screen.
      sig.current.phase += dt * 3.2;
      if (sctx) {
        sigBuf.current.push(signalAt(sig.current.phase));
        if (sigBuf.current.length > sc.width) sigBuf.current.shift();
        sctx.clearRect(0, 0, sc.width, sc.height);
        sctx.strokeStyle = BLUE; sctx.lineWidth = 2; sctx.lineJoin = "round";
        sctx.shadowColor = BLUE; sctx.shadowBlur = 5; sctx.beginPath();
        const buf = sigBuf.current, startI = Math.max(0, buf.length - sc.width);
        for (let i = startI; i < buf.length; i++) {
          const x = i - startI, y = sc.height / 2 - buf[i] * (sc.height / 2 - 3);
          if (i === startI) sctx.moveTo(x, y); else sctx.lineTo(x, y);
        }
        sctx.stroke(); sctx.shadowBlur = 0;
      }

      if (clockRef.current) clockRef.current.childNodes[0].textContent = String(Math.max(0, Math.ceil(DUR - elapsed)));

      // The scan is "ready" only once the SDK has produced frames AND a short
      // warmup grace has passed — until then show the loader, never the
      // "face not detected" warning.
      const ready = faceOK || (framesSeen && firstFrameMs && now - firstFrameMs > WARMUP_MS);
      if (loaderRef.current) loaderRef.current.style.display = ready || clock.current.finished ? "none" : "";
      const faceMissing = ready && !faceOK && !clock.current.finished;
      if (faceWarnRef.current) faceWarnRef.current.style.display = faceMissing ? "flex" : "none";
      if (hudStateRef.current) hudStateRef.current.textContent = clock.current.finished ? L.done : faceOK ? L.measuring : ready ? L.alignFace : L.starting;
      if (promptRef.current) promptRef.current.style.display = faceMissing || !ready ? "none" : "block";

      // horizontal progress bar + small completion %
      if (fillRef.current) fillRef.current.style.width = pct + "%";
      if (pctRef.current) pctRef.current.textContent = Math.round(pct) + "%";

      // SDK reached full duration but onScanFinish hasn't fired yet → bounded wait
      if (elapsed >= DUR && !clock.current.finished && !sdkFinishWatch) {
        sdkFinishWatch = later(() => { if (!clock.current.finished) failScan(tr("scan.errFinishTime")); }, 15000);
      }

      // Mid-scan freeze / feed-death guards (only during the live scan)
      if (!clock.current.finished && elapsed < DUR) {
        if (!faceOK || elapsed > stall.elapsed + 0.05) {
          stall.elapsed = Math.max(stall.elapsed, elapsed);
          stall.atMs = now;
        } else if (sdkProgressed && now - stall.atMs > 14000) {
          failScan(tr("scan.errFroze"));
        }
        if (sdkProgressed && lastFrameMs && now - lastFrameMs > 8000) {
          failScan(tr("scan.errFeedStop"));
        }
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      aborted = true;
      cancelAnimationFrame(raf);
      clearTimeout(startTimer);
      timers.forEach(clearTimeout);
      try { if (origGUM) navigator.mediaDevices.getUserMedia = origGUM; } catch (_) {}
      try { facescan.stopScan(); } catch (_) {}
    };
  }, [phase, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRetry = () => {
    if (prefill) setPhase("boot");
    else if (phase === "scan") setPhase("identify");
    setScanError(null); setRetryKey((k) => k + 1);
  };

  const footHint = phase === "boot"
    ? tr("scan.opening")
    : phase === "scan"
    ? tr("scan.capturingHint")
    : manual ? tr("identify.manualHint")
    : idState === "matched" ? tr("identify.startHint")
    : idState === "notfound" ? tr("identify.notFound")
    : tr("identify.waitHint");

  return (
    <div className="oh-view oh-scanview">
      <div className="oh-scanhead">
        <div className="oh-kicker">
          {phase === "identify" && onBack && (
            <button type="button" className="oh-backbtn" onClick={onBack} aria-label={tr("common.back")}>‹ {tr("common.back")}</button>
          )}
          {phase === "identify" ? tr("identify.kicker") : tr("scan.kicker")}
        </div>
        {phase === "scan" && <div className="oh-clock oh-mono" ref={clockRef}>{DUR}<small>{tr("scan.seconds")}</small></div>}
      </div>

      <div className={"oh-cam" + (onlineIdentity && phase === "identify" && idState === "online" ? " oh-online-id-entry" : "")}>
        {/* hidden SDK work canvas */}
        <canvas ref={canvasRef} width={640} height={480} style={{ position: "fixed", top: -2000, left: -2000, width: 640, height: 480, opacity: 0, pointerEvents: "none" }} />

        {/* full-view mirrored camera showing the worker's face (identify + scan) */}
        <video ref={videoRef} autoPlay muted playsInline />

        {/* 4-corner face-alignment frame */}
        <div className="oh-faceguide" ref={guideRef}>
          <div className="oh-frame"><i /><i /><i /><i /></div>
        </div>

        {/* ── BOOT overlay (embed mode: creating scan session) ── */}
        {phase === "boot" && !scanError && (
          <div className="oh-camloader">
            <div className="oh-spin" />
            <p>{tr("scan.opening")}<small>{tr("scan.openingSub")}</small></p>
          </div>
        )}

        {/* ── IDENTIFY overlays ── */}
        {phase === "identify" && !scanError && (
          manual ? (
              <div className={"oh-manual" + (onlineIdentity ? " oh-manual-online" : "")}>
              <div className="oh-manual-title">{matched ? tr("identify.verified") + " — " + (matched.name || "") : registering ? tr("identify.registerTitle") : tr("identify.manualTitle")}</div>
              {registering && <div className="oh-manual-why">{tr("identify.manualWhy")}</div>}
              {registering && (
                /* Kiosk-style photo first: the live camera runs behind this
                   overlay, so the capture works off the main preview stream.
                   The framed thumbnail confirms the shot before submitting. */
                <div className="oh-regshot-row">
                  <div className="oh-regshot">
                    {regPhoto
                      ? <img src={regPhoto} alt="" />
                      : <video autoPlay muted playsInline ref={shotVideoRef} />}
                  </div>
                  <div className="oh-regshot-side">
                    <small>{tr("identify.photoHint")}</small>
                    <button type="button" className={"oh-btn oh-btn-manual sm" + (regPhoto ? " ghost" : "")}
                      disabled={onlineBusy}
                      onClick={() => {
                        if (regPhoto) {
                          setRegPhoto(null);
                          capturedFaceRef.current = null;
                        } else {
                          captureRegistrationPhoto();
                        }
                      }}>
                      {onlineBusy ? tr("identify.capturingPhoto") : regPhoto ? tr("identify.takeAgain") : tr("identify.takePhoto")}
                    </button>
                  </div>
                </div>
              )}
              {registering && (
                <label className="oh-field oh-field-wide">
                  <span>Employee ID</span>
                  <input type="text" value={md.empId} onChange={(e) => setMd({ ...md, empId: e.target.value })} autoComplete="off" placeholder="EMP-0042" />
                </label>
              )}
              <label className="oh-field oh-field-wide">
                <span>{tr("identify.fName")}</span>
                <input type="text" value={md.name} readOnly={!!matched} onChange={(e) => setMd({ ...md, name: e.target.value })} autoComplete="off" />
              </label>
              <div className="oh-field oh-field-wide">
                <span>{tr("identify.fGender")}</span>
                <div className="oh-genders">
                  {["Male", "Female", "Other"].map((g) => (
                    <button type="button" key={g}
                      className={"oh-gender" + (md.gender === g ? " on" : "")}
                      onClick={() => setMd({ ...md, gender: g })}>
                      {tr("identify.g" + g)}
                    </button>
                  ))}
                </div>
              </div>
              {registering && (
                <div className="oh-manual-row">
                  <div className="oh-field" style={{ flex: 2 }}>
                    <span>{tr("identify.ageOrDob")}</span>
                    <div className="oh-unit-toggle">
                      <button type="button" className={ageEntryMode === "dob" ? "on" : ""} onClick={() => setAgeEntryMode("dob")}>{tr("identify.dobEntry")}</button>
                      <button type="button" className={ageEntryMode === "age" ? "on" : ""} onClick={() => setAgeEntryMode("age")}>{tr("identify.ageEntry")}</button>
                    </div>
                    {ageEntryMode === "dob" ? (
                      <input type="date" value={md.dob} max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setMd({ ...md, dob: e.target.value })} />
                    ) : (
                      <input type="number" inputMode="numeric" min="1" max="119" value={md.age}
                        onChange={(e) => setMd({ ...md, age: e.target.value })} />
                    )}
                    {ageEntryMode === "dob" && dobAge != null && <small className="oh-field-note">{tr("identify.computedAge")} {dobAge}</small>}
                  </div>
                  <label className="oh-field" style={{ flex: 2 }}>
                    <span>{tr("identify.contact")}</span>
                    <input type="tel" inputMode="tel" value={md.contact} onChange={(e) => setMd({ ...md, contact: e.target.value })} autoComplete="off" />
                  </label>
                </div>
              )}
              <div className="oh-manual-row">
                {!registering && (
                <label className="oh-field">
                  <span>{tr("identify.fAge")}</span>
                  <input type="number" inputMode="numeric" min="1" value={md.age} onChange={(e) => setMd({ ...md, age: e.target.value })} />
                </label>
                )}
                <div className="oh-field">
                  <span>{tr("identify.fHeight")}</span>
                  {registering && (
                    <div className="oh-unit-toggle">
                      <button type="button" className={heightUnit === "cm" ? "on" : ""} onClick={() => setHeightUnit("cm")}>cm</button>
                      <button type="button" className={heightUnit === "imperial" ? "on" : ""} onClick={() => setHeightUnit("imperial")}>ft/in</button>
                    </div>
                  )}
                  {registering && heightUnit === "imperial" ? (
                    <div className="oh-height-pair">
                      <input aria-label={tr("identify.feet")} type="number" inputMode="numeric" min="1" max="8"
                        placeholder={tr("identify.feet")} value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)} />
                      <input aria-label={tr("identify.inches")} type="number" inputMode="numeric" min="0" max="11"
                        placeholder={tr("identify.inches")} value={heightInches} onChange={(e) => setHeightInches(e.target.value)} />
                    </div>
                  ) : (
                    <input type="number" inputMode="numeric" min="1" value={md.height} onChange={(e) => setMd({ ...md, height: e.target.value })} />
                  )}
                </div>
                <label className="oh-field">
                  <span>{tr("identify.fWeight")}</span>
                  <input type="number" inputMode="numeric" min="1" value={md.weight} onChange={(e) => setMd({ ...md, weight: e.target.value })} />
                </label>
              </div>
              {matched ? (
                /* Assessment set comes from the admin dashboard for this worker —
                   shown read-only, not toggleable at the kiosk. */
                !onlineIdentity && <div className="oh-screenings">
                  <div className="oh-screenings-title">{tr("identify.screeningTitle")}</div>
                  <div className="oh-assess-list">
                    <span className="oh-assess on">Face vitals scan</span>
                    <span className={"oh-assess" + (matched.pulmoRequired ? " on" : "")}>{tr("identify.togglePulmo")}</span>
                    <span className={"oh-assess" + (matched.cognitiveRequired ? " on" : "")}>{tr("identify.toggleCognitive")}</span>
                  </div>
                </div>
              ) : deptRequired ? (
                /* Device mode: an unregistered worker is tagged to a department and
                   inherits the department's assessment set (no kiosk toggles). */
                <>
                  <div className="oh-fixed-site">
                    <span>{tr("identify.siteLabel")}</span>
                    <strong>{siteName || tr("identify.fixedSiteLoading")}</strong>
                  </div>
                  {depts === null ? (
                    <div className="oh-dept-state"><div className="oh-spin" />{tr("identify.deptLoading")}</div>
                  ) : deptError ? (
                    <div className="oh-dept-state error" role="alert">
                      <span>{deptError}</span>
                      <button type="button" className="oh-btn ghost sm" onClick={() => setDeptRetry((n) => n + 1)}>{tr("identify.retryDepartments")}</button>
                    </div>
                  ) : (
                    <label className="oh-field oh-field-wide">
                      <span>{tr("identify.departmentLabel")}</span>
                      <select className="oh-select" value={md.dept} onChange={(e) => setMd({ ...md, dept: e.target.value })}>
                        <option value="">{tr("identify.deptSelect")}</option>
                        {depts.map((d) => (
                          <option key={d.departmentId} value={String(d.departmentId)}>{d.name}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {!onlineIdentity && <div className="oh-screenings">
                    <div className="oh-screenings-title">{tr("identify.screeningTitle")}</div>
                    {selectedDept ? (
                      <div className="oh-assess-list">
                        <span className="oh-assess on">Face vitals scan</span>
                        <span className={"oh-assess" + (selectedDept.pulmoRequired ? " on" : "")}>{tr("identify.togglePulmo")}</span>
                        <span className={"oh-assess" + (selectedDept.cognitiveRequired ? " on" : "")}>{tr("identify.toggleCognitive")}</span>
                      </div>
                    ) : (
                      <small className="oh-empid-msg">Select a department to see its assessment set</small>
                    )}
                  </div>}
                </>
              ) : !onlineIdentity ? (
                <div className="oh-screenings">
                  <div className="oh-screenings-title">{tr("identify.screeningTitle")}</div>
                  <button type="button" className={"oh-toggle" + (md.pulmo ? " on" : "")} onClick={() => setMd({ ...md, pulmo: !md.pulmo })} aria-pressed={md.pulmo}>
                    <span className="oh-toggle-label">{tr("identify.togglePulmo")}</span>
                    <span className="oh-toggle-track"><span className="oh-toggle-knob" /></span>
                  </button>
                  <button type="button" className={"oh-toggle" + (md.cognitive ? " on" : "")} onClick={() => setMd({ ...md, cognitive: !md.cognitive })} aria-pressed={md.cognitive}>
                    <span className="oh-toggle-label">{tr("identify.toggleCognitive")}</span>
                    <span className="oh-toggle-track"><span className="oh-toggle-knob" /></span>
                  </button>
                </div>
              ) : null}
              {obErr && <small className="oh-empid-msg" style={{ display: "block", marginTop: 8 }}>{obErr}</small>}
              <div className="oh-manual-actions">
                <button type="button" className="oh-btn ghost sm oh-manual-back" onClick={() => {
                  setObErr("");
                  if (onlineIdentity) {
                    stopPreview();
                    onBack?.();
                  } else {
                    setManual(false);
                  }
                }}>
                  ‹ {tr("common.back")}
                </button>
                <button type="button" className="oh-btn oh-manual-submit" disabled={!manualValid || starting} onClick={startScan}>
                  {starting ? tr("identify.starting") : registering ? tr("identify.registerStart") : tr("identify.startScan")}
                </button>
              </div>
            </div>
          ) : idState === "matched" ? (
            <div className="oh-idcard">
              {matched?.photoObjUrl ? (
                <img className="oh-idphoto" src={matched.photoObjUrl} alt="" />
              ) : (
                <div className="oh-idcheck">✓</div>
              )}
              <div className="oh-idverified">{tr("identify.verified")}</div>
              <div className="oh-idname">{matched?.name}</div>
              <div className="oh-idbadge">{tr("identify.badgeLabel")} · {matched?.employeeId}</div>
              <div className="oh-idactions">
                <button className="oh-btn oh-btn-manual sm" onClick={() => setManual(true)}>{tr("common.continue")}</button>
                <button className="oh-btn ghost sm" onClick={onRetry}>{tr("common.tryAgain")}</button>
              </div>
            </div>
          ) : idState === "capture" ? (
            <div className="oh-online-capture">
              <div className="oh-online-capture-copy">
                <strong>{tr("identify.photoTitle")}</strong>
                <span>{tr("identify.photoHint")}</span>
              </div>
              {onlineErr && <small className="oh-empid-msg" role="alert">{onlineErr}</small>}
              {!showCaptureFallback && (
                <div className="oh-auto-capture-status"><span />{tr("identify.capturingPhoto")}</div>
              )}
              {showCaptureFallback && (
                <button type="button" className="oh-btn oh-btn-manual"
                  disabled={onlineBusy} onClick={captureOnlineIdentityPhoto}>
                  {onlineBusy ? tr("identify.capturingPhoto") : tr("identify.takePhoto")}
                </button>
              )}
              <button type="button" className="oh-btn ghost oh-capture-register" disabled={onlineBusy} onClick={enterRegistration}>
                {tr("identify.registerButton")}
              </button>
            </div>
          ) : idState === "online" ? (
            <div className="oh-idcard oh-online-idcard">
              {regPhoto && <img className="oh-online-captured-photo" src={regPhoto} alt="" />}
              <div className="oh-idverified">{tr("identify.onlineTitle")}</div>
              <div className="oh-idname oh-idname-sm">{tr("identify.onlineSub")}</div>
              <label className="oh-field oh-field-wide">
                <span>{tr("identify.employeeId")}</span>
                <input type="text" value={onlineEmpId}
                  onChange={(e) => { setOnlineEmpId(e.target.value); setOnlineErr(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") checkOnlineWorker(); }}
                  autoCapitalize="characters" autoComplete="off" placeholder="EMP-0042" />
              </label>
              {onlineErr && <small className="oh-empid-msg" role="alert">{onlineErr}</small>}
              <div className="oh-idactions">
                <button type="button" className="oh-btn oh-btn-manual sm"
                  disabled={!onlineEmpId.trim() || onlineBusy || starting} onClick={checkOnlineWorker}>
                  {onlineBusy || starting ? tr("identify.onlineChecking") : tr("identify.onlineCheck")}
                </button>
                <button type="button" className="oh-btn ghost sm" disabled={onlineBusy || starting} onClick={onRetry}>
                  {tr("identify.takeAgain")}
                </button>
              </div>
            </div>
          ) : idState === "matching" ? (
            <div className="oh-idscan">
              <div className="oh-idsweep" />
              <p>{tr(onlineIdentity ? "identify.photoTitle" : "identify.matching")}
                <small>{tr(onlineIdentity ? "identify.photoHint" : "identify.matchingSub")}</small>
              </p>
              {!!getDeviceKey() && (
                <button type="button" className="oh-reglink" onClick={() => setManual(true)}>
                  {tr("identify.firstTime")}
                </button>
              )}
            </div>
          ) : (
            <div className="oh-idcard">
              <div className="oh-idcheck oh-warn">!</div>
              <div className="oh-idverified">{tr("identify.notFoundKicker")}</div>
              <div className="oh-idname oh-idname-sm">{tr("identify.notFound")}</div>
              <div className="oh-idactions">
                <button className="oh-btn oh-btn-manual sm" onClick={onRetry}>{tr("common.tryAgain")}</button>
                <button className="oh-btn ghost sm" onClick={() => setManual(true)}>{getDeviceKey() ? tr("identify.firstTime") : tr("identify.enterManually")}</button>
              </div>
            </div>
          )
        )}

        {/* ── SCAN overlays (no fabricated heart rate) ── */}
        {phase === "scan" && (
          <>
            <div className="oh-hud">
              <div className="oh-hud-row">
                <div className="oh-measure"><span className="oh-measure-dot" />{tr("scan.measuring")}</div>
                <canvas ref={sigRef} width={168} height={30} />
              </div>
              <div className="st"><span>{tr("scan.liveSignal").toUpperCase()}</span><b ref={hudStateRef}>{tr("scan.alignFace").toUpperCase()}</b></div>
              <div className="oh-progress">
                <div className="oh-progress-track"><div className="oh-progress-fill" ref={fillRef} /></div>
                <b className="oh-progress-pct" ref={pctRef}>0%</b>
              </div>
            </div>

            <div className="oh-scan-prompt" ref={promptRef}>{tr("scan.prompt")}</div>

            <div className="oh-facewarn" ref={faceWarnRef}>
              <p>{tr("scan.centerFace")}<small>{tr("scan.noFace")}</small></p>
            </div>

            <div className="oh-camloader" ref={loaderRef}>
              <div className="oh-spin" />
              <p>{tr("scan.opening")}<small>{tr("scan.openingSub")}</small></p>
            </div>

            {analyzing && (
              <div className="oh-analyzing">
                <div className="oh-spin" />
                <p>{tr("scan.reading")}</p>
              </div>
            )}
          </>
        )}

        {scanError && (
          <div className="oh-errbox">
            <div className="oh-errcard">
              <div className="em">{isEmbedded ? "📷" : "⚠️"}</div>
              <h3>{isEmbedded ? tr("scan.embedTitle") : tr("scan.errTitle")}</h3>
              <p>{isEmbedded ? tr("scan.embedMsg") : scanError}</p>
              {isEmbedded ? (
                <>
                  <button className="oh-btn" style={{ marginTop: 18 }} onClick={() => window.open(window.location.href, "_blank", "noopener")}>{tr("scan.openTab")}</button>
                  <button className="oh-btn ghost" style={{ marginTop: 10 }} onClick={onRetry}>{tr("common.tryAgain")}</button>
                </>
              ) : (
                <button className="oh-btn" style={{ marginTop: 18 }} onClick={onRetry}>{tr("common.tryAgain")}</button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* footer: identify → Start face scan · scan → screening-path choice */}
      {phase === "identify" ? (
        /* The Submit/Register button now lives inside the form itself — the
           footer only carries the hint while the form is open. */
        <p className="oh-scan-choicehint">{footHint}</p>
      ) : (
        <p className="oh-scan-choicehint">{footHint}</p>
      )}
    </div>
  );
}
